// src/pages/NewOrderPage.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./NewOrderPage.css";
import { API_URL } from "../config";

function NewOrderPage({ currentUser }) {
  const navigate = useNavigate();
  const [areas, setAreas] = useState([]);
  const [clients, setClients] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [error, setError] = useState("");

  /* ---------- KONFIG „Rodzaj Zlecenia” ---------- */
  // kompletna lista dla Admina
  const ORDER_TYPES_ADMIN  = [
	"Awaria",
    "Wewnętrzne",
    "Usługa",
	"Reklamacja",
	"Przegląd A",
	"Przegląd Z", 
  ];
  // lista skrócona dla pozostałych użytkowników
  const ORDER_TYPES_USER   = [
    "Awaria",
    "Wewnętrzne",
	"Usługa",
    "Reklamacja",
  ];
  // wybór listy zależnie od roli
  const orderTypeOptions =
    currentUser && currentUser.role === "Admin"
      ? ORDER_TYPES_ADMIN
      : ORDER_TYPES_USER;

  /* ---------- STAN FORMULARZA ---------- */
  const [formData, setFormData] = useState({
    tytul: "",
    opis_zlecenia: "",
    osoba_wprowadzajaca_id: currentUser ? currentUser.id : "",
    rodzaj_zgloszenia: orderTypeOptions[0],   // domyślny pierwszy z listy
    waga: "Neutralny",
    deadline: "",                 // termin wykonania
    osoba_wykonujaca_id: "",
    obszar_id: "",
    klient_id: "",
    osoba_zlecajaca: "",
    nr_klienta: ""
  });

  /* ---------- POBIERANIE DANYCH ---------- */
  useEffect(() => {
    fetchAreas();
    fetchClients();
    fetchWorkers();
  }, []);

  const fetchAreas = async () => {
    try {
      const response = await fetch(
        `${API_URL}/orders/areas`,
        { credentials: "include" }
      );
      if (!response.ok) throw new Error("Błąd podczas pobierania obszarów");
      const data = await response.json();
      setAreas(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await fetch(
        `${API_URL}/orders/clients`,
        { credentials: "include" }
      );
      if (!response.ok) throw new Error("Błąd podczas pobierania klientów");
      const data = await response.json();
      setClients(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchWorkers = async () => {
    try {
      const response = await fetch(
        `${API_URL}/orders/workers`,
        { credentials: "include" }
      );
      if (!response.ok) throw new Error("Błąd podczas pobierania wykonawców");
      const data = await response.json();
      setWorkers(data);
    } catch (err) {
      console.error(err);
    }
  };

  /* ---------- HANDLERY ---------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const today = new Date().toISOString().split("T")[0];
    const orderData = { ...formData, data_zlecenia: today };

    if (
      !orderData.tytul ||
      !orderData.osoba_wprowadzajaca_id ||
      !orderData.rodzaj_zgloszenia ||
      !orderData.waga ||
      !orderData.osoba_wykonujaca_id
    ) {
      setError("Wypełnij wszystkie wymagane pola.");
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/orders`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(orderData)
        }
      );
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Błąd podczas tworzenia zlecenia.");
      }
      const newOrder = await response.json();
      navigate(`/zlecenia/${newOrder.id}`);
    } catch (err) {
      console.error(err);
      setError(err.message);
    }
  };

  /* ---------- RENDER ---------- */
  return (
    <div className="new-order-page">
      <h2>Nowe Zlecenie</h2>
      {error && <p className="error-message">{error}</p>}
      <form onSubmit={handleSubmit} className="new-order-form">
        <div className="form-group">
          <label>Tytuł Zlecenia:</label>
          <input
            type="text"
            name="tytul"
            value={formData.tytul}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Treść Zlecenia:</label>
          <textarea
            name="opis_zlecenia"
            value={formData.opis_zlecenia}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>Priorytet:</label>
          <select
            name="waga"
            value={formData.waga}
            onChange={handleChange}
            required
          >
            <option value="Bardzo Niski">Bardzo Niski</option>
            <option value="Niski">Niski</option>
            <option value="Neutralny">Neutralny</option>
            <option value="Wysoki">Wysoki</option>
            <option value="Krytyczny">Krytyczny</option>
          </select>
        </div>

        <div className="form-group">
          <label>Rodzaj Zlecenia:</label>
          <select
            name="rodzaj_zgloszenia"
            value={formData.rodzaj_zgloszenia}
            onChange={handleChange}
          >
            {orderTypeOptions.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Termin Wykonania (Deadline):</label>
          <input
            type="date"
            name="deadline"
            value={formData.deadline}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Wykonawca Zlecenia:</label>
          <select
            name="osoba_wykonujaca_id"
            value={formData.osoba_wykonujaca_id}
            onChange={handleChange}
            required
          >
            <option value="">-- Wybierz wykonawcę --</option>
            {workers.map(worker => (
              <option key={worker.id} value={worker.id}>
                {worker.first_name} {worker.last_name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Obszar:</label>
          <select
            name="obszar_id"
            value={formData.obszar_id}
            onChange={handleChange}
          >
            <option value="">-- Wybierz obszar --</option>
            {areas.map(area => (
              <option key={area.id} value={area.id}>{area.name}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Klient:</label>
          <select
            name="klient_id"
            value={formData.klient_id}
            onChange={handleChange}
          >
            <option value="">-- Wybierz klienta --</option>
            {clients.map(client => (
              <option key={client.id} value={client.id}>{client.name}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Zleceniodawca:</label>
          <input
            type="text"
            name="osoba_zlecajaca"
            value={formData.osoba_zlecajaca}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>Numer Klienta (opcjonalnie):</label>
          <input
            type="text"
            name="nr_klienta"
            value={formData.nr_klienta}
            onChange={handleChange}
          />
        </div>

        <button type="submit" className="btn-submit">
          Utwórz Zlecenie
        </button>
      </form>
    </div>
  );
}

export default NewOrderPage;
